import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  DollarSign, ShoppingCart, Users, Clock, TrendingUp, TrendingDown,
  ArrowUpRight, AlertTriangle, RefreshCw, Plus
} from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const money = (n) => `$${(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const STATUS_META = {
  pending: { label: 'Pending', color: '#F59E0B' },
  processing: { label: 'Processing', color: '#3B82F6' },
  ready_to_ship: { label: 'Ready to Ship', color: '#6366F1' },
  shipped: { label: 'Shipped', color: '#8B5CF6' },
  in_transit: { label: 'In Transit', color: '#8B5CF6' },
  delivered: { label: 'Delivered', color: '#10B981' },
  completed: { label: 'Completed', color: '#10B981' },
  cancelled: { label: 'Cancelled', color: '#EF4444' },
  unknown: { label: 'Other', color: '#64748B' },
};
const statusMeta = (s) => STATUS_META[s] || { label: (s || '').charAt(0).toUpperCase() + (s || '').slice(1), color: '#64748B' };

const greeting = () => {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
};

const Card = ({ className = '', children, testid }) => (
  <div data-testid={testid} className={`bg-[#14171B] border border-white/[0.06] rounded-2xl ${className}`}>{children}</div>
);
const Skeleton = ({ className = '' }) => <div className={`animate-pulse bg-white/[0.05] rounded-lg ${className}`} />;

const ChangeBadge = ({ pct }) => {
  if (pct === null || pct === undefined) return null;
  const up = pct >= 0;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium ${up ? 'text-emerald-400' : 'text-red-400'}`}>
      {up ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
      {up ? '+' : ''}{pct}% <span className="text-slate-500 font-normal">vs last month</span>
    </span>
  );
};

const KpiCard = ({ icon: Icon, label, value, change, accent, testid }) => (
  <Card className="p-5" testid={testid}>
    <div className="flex items-start justify-between">
      <div className="min-w-0">
        <p className="text-slate-400 text-xs uppercase tracking-wider mb-2">{label}</p>
        <p className="text-white text-[28px] leading-none font-semibold tracking-tight">{value}</p>
        <div className="mt-2"><ChangeBadge pct={change} /></div>
      </div>
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${accent}`}>
        <Icon className="w-5 h-5" />
      </div>
    </div>
  </Card>
);

const SalesAreaChart = ({ series }) => {
  const W = 700, H = 240, PADX = 16, PADB = 28, PADT = 16;
  const data = series || [];
  const max = Math.max(1, ...data.map((d) => d.revenue));
  const n = data.length;
  const xFor = (i) => PADX + (n <= 1 ? 0 : (i / (n - 1)) * (W - PADX * 2));
  const yFor = (v) => PADT + (1 - v / max) * (H - PADT - PADB);
  const pts = data.map((d, i) => [xFor(i), yFor(d.revenue)]);
  const line = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');
  const area = pts.length ? `${line} L${xFor(n - 1).toFixed(1)},${H - PADB} L${xFor(0).toFixed(1)},${H - PADB} Z` : '';
  const hasData = data.some((d) => d.revenue > 0);
  return (
    <div className="w-full">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-[240px]" preserveAspectRatio="none">
        <defs>
          <linearGradient id="srGoldFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#C6A15B" stopOpacity="0.28" />
            <stop offset="100%" stopColor="#C6A15B" stopOpacity="0" />
          </linearGradient>
        </defs>
        {[0.25, 0.5, 0.75, 1].map((g, i) => (
          <line key={i} x1={PADX} x2={W - PADX} y1={PADT + g * (H - PADT - PADB)} y2={PADT + g * (H - PADT - PADB)}
            stroke="#ffffff" strokeOpacity="0.05" strokeWidth="1" />
        ))}
        {hasData && <path d={area} fill="url(#srGoldFill)" />}
        {hasData && <path d={line} fill="none" stroke="#C6A15B" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />}
        {hasData && pts.map((p, i) => (
          <g key={i}>
            <circle cx={p[0]} cy={p[1]} r="3.5" fill="#0C0E11" stroke="#C6A15B" strokeWidth="2" />
            <title>{`${data[i].label}: ${money(data[i].revenue)}`}</title>
          </g>
        ))}
        {data.map((d, i) => (
          <text key={i} x={xFor(i)} y={H - 8} textAnchor="middle" fontSize="11" fill="#64748B">{d.label.split(' ')[0]}</text>
        ))}
      </svg>
      {!hasData && <p className="text-center text-slate-500 text-sm -mt-28 relative z-10 pb-24">No sales recorded for this period.</p>}
    </div>
  );
};

const SectionTitle = ({ children, action }) => (
  <div className="flex items-center justify-between mb-4">
    <h3 className="text-white font-semibold">{children}</h3>
    {action}
  </div>
);
const ViewLink = ({ onClick, children }) => (
  <button onClick={onClick} className="inline-flex items-center gap-1 text-xs text-[#C6A15B] hover:text-[#E7CB85] transition-colors">
    {children} <ArrowUpRight className="w-3.5 h-3.5" />
  </button>
);

export const SalesDashboard = ({ user, setActiveTab }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [period, setPeriod] = useState('6m');

  const load = async (p = period) => {
    setLoading(true); setError(false);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/sales-rep/dashboard?period=${p}`);
      setData(res.data);
    } catch (e) {
      setError(true);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(period); /* eslint-disable-next-line */ }, [period]);

  const k = data?.kpis || {};
  const periods = [{ k: '30d', l: '30 Days' }, { k: '3m', l: '3 Months' }, { k: '6m', l: '6 Months' }, { k: '12m', l: '12 Months' }];
  const statusEntries = Object.entries(data?.status_breakdown || {}).filter(([, v]) => v > 0);

  return (
    <div className="space-y-6" data-testid="sales-rep-dashboard">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl border border-white/[0.06] bg-gradient-to-br from-[#14171B] via-[#111418] to-[#0C0E11] px-6 py-6">
        <div className="absolute -right-10 -top-16 w-72 h-72 rounded-full bg-[#C6A15B]/10 blur-3xl pointer-events-none" />
        <div className="flex items-start justify-between gap-4 relative">
          <div>
            <p className="text-slate-400 text-sm">{greeting()},</p>
            <h2 className="text-2xl sm:text-3xl font-semibold text-white mt-0.5">{user?.full_name || user?.username || 'Sales Representative'}</h2>
            <p className="text-slate-500 text-sm mt-1">Here's what's happening with your accounts.</p>
          </div>
          <button onClick={() => setActiveTab('create-order')} data-testid="sr-hero-create-order"
            className="hidden sm:inline-flex items-center gap-2 bg-[#C6A15B] hover:bg-[#d8b36c] text-black font-medium text-sm px-4 py-2.5 rounded-xl transition-colors shrink-0">
            <Plus className="w-4 h-4" /> Create Order
          </button>
        </div>
      </div>

      {error && (
        <Card className="p-5 flex items-center justify-between" testid="dashboard-error">
          <span className="text-red-300 text-sm flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Unable to load your dashboard.</span>
          <button onClick={() => load()} className="inline-flex items-center gap-1.5 text-xs bg-white/[0.05] hover:bg-white/[0.1] text-white px-3 py-1.5 rounded-lg">
            <RefreshCw className="w-3.5 h-3.5" /> Retry
          </button>
        </Card>
      )}

      {/* KPI row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {loading ? [0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-[120px]" />) : (
          <>
            <KpiCard testid="kpi-my-sales" icon={DollarSign} label="My Sales" value={money(k.my_sales)}
              change={k.sales_change_pct} accent="bg-[#C6A15B]/15 text-[#C6A15B]" />
            <KpiCard testid="kpi-my-orders" icon={ShoppingCart} label="My Orders" value={k.my_orders}
              accent="bg-blue-500/15 text-blue-400" />
            <KpiCard testid="kpi-my-clients" icon={Users} label="My Clients" value={k.my_clients}
              accent="bg-emerald-500/15 text-emerald-400" />
            <KpiCard testid="kpi-open-orders" icon={Clock} label="Open Orders" value={k.open_orders}
              accent="bg-amber-500/15 text-amber-400" />
          </>
        )}
      </div>

      {/* Row 2: sales chart + order status */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <Card className="p-5 lg:col-span-8" testid="sr-sales-chart-card">
          <SectionTitle action={
            <div className="flex gap-1 bg-black/30 rounded-lg p-0.5">
              {periods.map((p) => (
                <button key={p.k} onClick={() => setPeriod(p.k)} data-testid={`sr-period-${p.k}`}
                  className={`px-2.5 py-1 rounded-md text-xs transition-colors ${period === p.k ? 'bg-[#C6A15B] text-black font-medium' : 'text-slate-400 hover:text-white'}`}>
                  {p.l}
                </button>
              ))}
            </div>
          }>My Sales</SectionTitle>
          {loading ? <Skeleton className="h-[240px]" /> : <SalesAreaChart series={data?.revenue_series} />}
        </Card>

        <Card className="p-5 lg:col-span-4" testid="sr-status-card">
          <SectionTitle action={<ViewLink onClick={() => setActiveTab('orders')}>View all</ViewLink>}>My Order Status</SectionTitle>
          {loading ? <Skeleton className="h-[160px]" /> : (
            statusEntries.length === 0 ? (
              <p className="text-slate-500 text-sm py-8 text-center">No orders yet.</p>
            ) : (
              <div className="space-y-2.5">
                {statusEntries.map(([s, v]) => {
                  const m = statusMeta(s);
                  return (
                    <div key={s} className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-2 text-slate-300">
                        <span className="w-2.5 h-2.5 rounded-full" style={{ background: m.color }} />
                        {m.label}
                      </span>
                      <span className="text-white font-medium">{v}</span>
                    </div>
                  );
                })}
              </div>
            )
          )}
        </Card>
      </div>

      {/* Row 3: recent orders */}
      <Card className="p-5" testid="sr-recent-orders-card">
        <SectionTitle action={<ViewLink onClick={() => setActiveTab('orders')}>View all</ViewLink>}>Recent Orders</SectionTitle>
        {loading ? <Skeleton className="h-[200px]" /> : (
          data?.recent_orders?.length ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-slate-500 text-xs border-b border-white/[0.06]">
                    <th className="text-left font-medium py-2">Order #</th>
                    <th className="text-left font-medium py-2">Customer</th>
                    <th className="text-left font-medium py-2 hidden sm:table-cell">Date</th>
                    <th className="text-right font-medium py-2">Total</th>
                    <th className="text-right font-medium py-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recent_orders.map((o) => {
                    const m = statusMeta(o.status);
                    return (
                      <tr key={o.order_id} className="border-b border-white/[0.04] hover:bg-white/[0.02] cursor-pointer"
                        onClick={() => setActiveTab('orders')} data-testid={`sr-recent-order-${o.order_number}`}>
                        <td className="py-2.5 font-mono text-[#C6A15B]">{o.order_number}</td>
                        <td className="py-2.5 text-slate-200 truncate max-w-[160px]">{o.customer}</td>
                        <td className="py-2.5 text-slate-500 hidden sm:table-cell">{o.date ? new Date(o.date).toLocaleDateString() : '—'}</td>
                        <td className="py-2.5 text-right text-white font-medium">{money(o.total)}</td>
                        <td className="py-2.5 text-right">
                          <span className="inline-block text-[11px] px-2 py-0.5 rounded-full" style={{ background: `${m.color}22`, color: m.color }}>{m.label}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center text-center py-10">
              <ShoppingCart className="w-8 h-8 text-slate-600 mb-2" />
              <p className="text-slate-300 text-sm">No orders yet.</p>
              <p className="text-slate-600 text-xs mt-1">Orders created for your clients will appear here.</p>
            </div>
          )
        )}
      </Card>
    </div>
  );
};

export default SalesDashboard;
